import React, { useEffect, useState } from 'react';
import { Users, Home, Crown, User } from 'lucide-react';
import clsx from 'clsx';
import { supabase } from '../../lib/supabase';

interface AgencyCaps {
  agency_id: string;
  membership_tier: string | null;
  max_agent_seats: number;
  agent_seats_used: number;
  max_standard_seats: number;
  standard_seats_used: number;
  max_pro_seats: number;
  pro_seats_used: number;
  max_properties: number;
  properties_published: number;
  properties_available: number;
  caller_publish_limit: number | null;
  caller_can_publish: boolean | null;
  caller_published: number | null;
  error?: string;
}

interface Props {
  theme: 'light' | 'dark' | 'high-contrast';
  show: 'seats' | 'properties' | 'both';
  refreshKey?: unknown;
}

function Chip({
  theme,
  icon,
  label,
  used,
  max,
  warnAt = 0.85,
  title,
}: {
  theme: Props['theme'];
  icon: React.ReactNode;
  label: string;
  used: number;
  max: number;
  warnAt?: number;
  title?: string;
}) {
  const isLight = theme !== 'dark';
  const ratio = max > 0 ? used / max : 0;
  const atLimit = max > 0 && used >= max;
  const warning = max > 0 && ratio >= warnAt && !atLimit;

  const tone = atLimit
    ? isLight
      ? 'bg-rose-50 border-rose-200 text-rose-700'
      : 'bg-rose-500/10 border-rose-500/30 text-rose-400'
    : warning
      ? isLight
        ? 'bg-amber-50 border-amber-200 text-amber-700'
        : 'bg-amber-500/10 border-amber-500/30 text-amber-400'
      : isLight
        ? 'bg-slate-50 border-slate-200 text-slate-700'
        : 'bg-white/5 border-white/10 text-slate-300';

  return (
    <div title={title} className={clsx('flex items-center gap-2 px-3 py-1.5 rounded-xl border text-xs font-medium', tone)}>
      <span className="shrink-0">{icon}</span>
      <span className="truncate">{label}:</span>
      <span className="font-bold">{used}/{max}</span>
    </div>
  );
}

export const AgencyCapsIndicator: React.FC<Props> = ({ theme, show, refreshKey }) => {
  const [caps, setCaps] = useState<AgencyCaps | null>(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data, error } = await supabase.rpc('get_agency_caps');
      if (cancelled) return;
      if (error || !data || (data as any).error) {
        setCaps(null);
        return;
      }
      setCaps(data as AgencyCaps);
    })();
    return () => {
      cancelled = true;
    };
  }, [refreshKey]);

  if (!caps) return null;

  return (
    <div className="flex flex-wrap items-center gap-2">
      {(show === 'seats' || show === 'both') && (
        <>
          <Chip
            theme={theme}
            icon={<Users size={13} />}
            label="Agentes Estándar"
            used={caps.standard_seats_used}
            max={caps.max_standard_seats}
          />
          <Chip
            theme={theme}
            icon={<Crown size={13} />}
            label="Agentes Pro"
            used={caps.pro_seats_used}
            max={caps.max_pro_seats}
          />
        </>
      )}
      {(show === 'properties' || show === 'both') && (
        <>
          <Chip
            theme={theme}
            icon={<Home size={13} />}
            label="Publicadas"
            used={caps.properties_published}
            max={caps.max_properties}
          />
          {/* Personal cap chip — only when the caller has a profile-level publish_limit
              that's stricter than the agency cap (otherwise the agency chip already tells
              them the binding number). */}
          {caps.caller_publish_limit != null
            && caps.caller_published != null
            && caps.caller_publish_limit < caps.max_properties && (
            <Chip
              theme={theme}
              icon={<User size={13} />}
              label="Mi límite"
              used={caps.caller_published}
              max={caps.caller_publish_limit}
              title="Límite personal de publicaciones de tu perfil. Pídele al administrador de la agencia que lo aumente si necesitas más."
            />
          )}
        </>
      )}
    </div>
  );
};
